import {announce} from '@github-ui/aria-live'
import {useCallback, useMemo, useRef, useState} from 'react'

import {describeRejectionClauses, describeRejections, type RejectedFile} from '../attachments/process-uploaded-files'

// Looser than the visible cap (`describeRejections` defaults to 5 names). Screen reader output has no
// layout to crowd, so it can afford to name more files before falling back to "and N more"; the
// banner stays tighter to protect the 400px side panel.
const ANNOUNCED_NAMES = 10

/**
 * Owns the state and the screen reader announcement for files an upload attempt rejected.
 *
 * `anchorRef` must point at an element inside the composer that the browser exposes to the
 * accessibility tree: `ariaNotify` ignores uninteresting elements, and the embedded surface renders
 * the composer in a `role="dialog"` where several screen readers miss a live region placed outside.
 */
export function useAttachmentRejections(
  anchorRef?: React.RefObject<HTMLElement | null>,
  bannerRef?: React.RefObject<HTMLElement | null>,
  focusComposer?: () => void,
) {
  const [rejections, setRejections] = useState<readonly RejectedFile[]>([])
  const rejectionsRef = useRef(rejections)
  const commit = useCallback((nextRejections: readonly RejectedFile[]) => {
    rejectionsRef.current = nextRejections
    setRejections(nextRejections)
  }, [])
  // Memoized so the controller object built from these messages keeps a stable identity between
  // renders that did not change the rejections.
  const messages = useMemo(() => describeRejectionClauses(rejections), [rejections])

  // A new attempt is the user action that clears prior feedback; an auto-dismiss would impose a
  // WCAG 2.2.1 time limit on reading a message that can run to several clauses.
  const beginAttempt = useCallback(() => {
    const banner = bannerRef?.current
    if (banner?.contains(banner.ownerDocument.activeElement)) focusComposer?.()
    commit([])
  }, [bannerRef, commit, focusComposer])
  const clearRejections = useCallback(
    (expected?: readonly RejectedFile[]) => {
      if (expected === undefined || rejectionsRef.current === expected) commit([])
    },
    [commit],
  )
  const snapshotRejections = useCallback(() => rejectionsRef.current, [])

  const reportRejections = useCallback(
    (nextRejections: readonly RejectedFile[]) => {
      if (nextRejections.length === 0) return
      commit([...rejectionsRef.current, ...nextRejections])

      const announcement = describeRejections(nextRejections, {maxNames: ANNOUNCED_NAMES})
      const anchor = anchorRef?.current
      if (announcement && anchor?.isConnected) {
        announce(announcement, {element: anchor})
      }
    },
    [anchorRef, commit],
  )

  return {rejections, reportRejections, beginAttempt, clearRejections, snapshotRejections, messages}
}
